import { Component, Input, forwardRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { GuidService } from '../../services/guid.service';

// 選項配置介面
export interface CheckboxOption {
  label: string;
  value: string;
  enableTextInput?: boolean;  // 是否啟用文字輸入
  textPlaceholder?: string;   // 文字輸入框的提示文字
  textRequired?: boolean;     // 文字輸入是否必填
}

// 擴充的 Category 介面以支援條件式文字輸入
export interface Category {
  name: string;       // 顯示用
  selected: boolean;  // 是否被勾選
  value?: string;     // 可能還需要保留原始 value
  enableTextInput?: boolean;  // 是否啟用文字輸入
  textPlaceholder?: string;   // 文字輸入框的提示文字
  textRequired?: boolean;     // 文字輸入是否必填
  textValue?: string;         // 文字輸入的值
}

// 元件輸出值介面
export interface CheckboxValue {
  option: string | { key: string; value: string };  // 選項值（可能是字串或物件）
  selected: boolean;   // 是否被選中
  textValue?: string;  // 文字輸入的值（如果有的話）
}

@Component({
  selector: 'app-form-multi-checkbox',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="form-section" *ngIf="shouldShow">
      <label [class.required-field]="required">{{ label }}</label>
      
      <!-- 只讀模式顯示 -->
      <div *ngIf="readonly" class="readonly-value">
        <div *ngIf="getSelectedOptions().length > 0; else noSelection" class="selected-options">
          <span *ngFor="let option of getSelectedOptions(); let last = last" class="selected-option">
            {{ option.name }}<span *ngIf="option.textValue">: {{ option.textValue }}</span><span *ngIf="!last">, </span>
          </span>
        </div>
        <ng-template #noSelection>-</ng-template>
      </div>
      
      <!-- 編輯模式 -->
      <div *ngIf="!readonly" class="checkbox-container">
        <div *ngFor="let category of categories; let i = index" class="checkbox-item">
          <input
            type="checkbox"
            [checked]="category.selected"
            (change)="toggleCategory(category, getChecked($event))"
            [id]="getUniqueId(category.name, i)"
            [attr.name]="name ? name + '-' + i : 'work-category-' + i" />
          <label [for]="getUniqueId(category.name, i)">{{ category.name }}</label>

          <!-- 條件式文字輸入框 -->
          <div *ngIf="category.enableTextInput && category.selected" class="text-input-container">
            <input type="text"
                   [(ngModel)]="category.textValue"
                   [placeholder]="category.textPlaceholder"
                   [required]="category.textRequired || false"
                   (input)="onTextValueChange(category)"
                   class="conditional-text-input form-input">
          </div>
        </div>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiCheckboxComponent),
      multi: true,
    },
  ],
  styleUrls: ['total-component.scss']
})
export class MultiCheckboxComponent implements ControlValueAccessor, OnInit {
  @Input() label: string = '缺失類別';
  @Input() name: string = ''; // 保留 name 屬性
  @Input() id: string = ''; // 新增 id 屬性
  @Input() required: boolean = false; // 是否必填
  @Input() readonly: boolean = false; // 新增：只讀模式
  @Input() enableWhereCondition: boolean = false; // 是否啟用條件顯示
  @Input() whereSourceComponent: string = ''; // 來源元件的 ID
  @Input() whereTargetValue: string = ''; // 觸發顯示的目標值
  @Input() sourceComponentValue: any = null; // 來源元件的值

  // 生成唯一的元件實例 ID
  private componentInstanceId: string;

  constructor(private guidService: GuidService) {
    this.componentInstanceId = this.guidService.generateGuid();
  }

  ngOnInit() {
    // 初始化時確保有唯一的實例 ID
    if (!this.componentInstanceId) {
      this.componentInstanceId = this.guidService.generateGuid();
    }
  }

  // 生成唯一的 ID
  getUniqueId(categoryName: string, index: number): string {
    return `checkbox-${this.componentInstanceId}-${index}-${categoryName.replace(/\s+/g, '-')}`;
  }

  /**
   * 新增 @Input() options，用於接收 JSON config 裡的選項。
   * 現在支援擴充的選項配置
   */
  @Input() set options(value: CheckboxOption[] | { label: string; value: string }[] | undefined) {
    if (!value) return;

    // 若外部尚未透過 writeValue 設定 categories，我們就初始化它
    if (!this._externalValueSet) {
      this.categories = value.map(opt => ({
        name: opt.label,
        value: opt.value,
        selected: false,
        enableTextInput: (opt as CheckboxOption).enableTextInput || false,
        textPlaceholder: (opt as CheckboxOption).textPlaceholder || '請輸入...',
        textRequired: (opt as CheckboxOption).textRequired || false,
        textValue: ''
      }));
    }
    this._rawOptions = value;
  }

  // 內部儲存原始 options 以防日後需要
  private _rawOptions: CheckboxOption[] | { label: string; value: string }[] = [];
  private _externalValueSet = false;

  categories: Category[] = [];

  onChange: (value: Category[] | CheckboxValue[]) => void = () => {};
  onTouched: () => void = () => {};

  // 從事件中取得 checked 屬性
  getChecked(event: Event): boolean {
    const target = event.target as HTMLInputElement;
    return target.checked;
  }

  toggleCategory(category: Category, checked: boolean): void {
    category.selected = checked;
    // 當取消勾選時，清空對應的文字值
    if (!checked && category.enableTextInput) {
      category.textValue = '';
    }
    this.emitValue();
  }

  // 處理文字輸入變化
  onTextValueChange(category: Category): void {
    this.emitValue();
  }

  // 統一的值輸出方法
  private emitValue(): void {
    // 根據是否有條件式文字輸入來決定輸出格式
    const hasConditionalInputs = this.categories.some(cat => cat.enableTextInput);

    if (hasConditionalInputs) {
      // 輸出擴充格式
      const checkboxValues: CheckboxValue[] = this.categories.map(cat => ({
        option: cat.value || cat.name,
        selected: cat.selected,
        textValue: cat.enableTextInput ? cat.textValue : undefined
      }));
      this.onChange(checkboxValues);
    } else {
      // 保持向後相容性，輸出原有格式
      this.onChange(this.categories);
    }
    this.onTouched();
  }

  // Reactive Form 寫值時會呼叫這裡
  writeValue(value: Category[] | CheckboxValue[] | any): void {
    console.log('[DEBUG] MultiCheckboxComponent writeValue:', value);
    console.log('[DEBUG] MultiCheckboxComponent writeValue - detailed:', JSON.stringify(value, null, 2));
    
    if (value) {
      this._externalValueSet = true;

      // 處理動態表單編輯模式的巢狀結構
      let checkboxValues: CheckboxValue[] | null = null;
      
      // 檢查是否是巢狀結構 { type: 'multi-checkbox', value: [...] }
      if (value.type === 'multi-checkbox' && Array.isArray(value.value)) {
        checkboxValues = value.value;
      } 
      // 檢查是否直接是陣列
      else if (Array.isArray(value)) {
        checkboxValues = value;
      }
      
      if (checkboxValues && checkboxValues.length > 0) {
        const firstItem = checkboxValues[0];

        // 如果是新格式 (CheckboxValue[])
        if ('option' in firstItem && 'selected' in firstItem) {
          this.categories = this._rawOptions.map(opt => {
            const correspondingValue = checkboxValues!.find(val => {
              // 處理 option 可能是物件或字串的情況
              if (typeof val.option === 'object' && val.option !== null && 'key' in val.option) {
                return val.option.key === opt.value;
              } else if (typeof val.option === 'string') {
                return val.option === opt.value || val.option === opt.label;
              }
              return false;
            });
            
            // 偵錯：記錄找到的對應值
            if (correspondingValue) {
              console.log(`[DEBUG] Found corresponding value for ${opt.value}:`, correspondingValue);
              console.log(`[DEBUG] textValue in correspondingValue:`, correspondingValue.textValue);
            }
            
            return {
              name: opt.label,
              value: opt.value,
              selected: correspondingValue?.selected || false,
              enableTextInput: (opt as CheckboxOption).enableTextInput || false,
              textPlaceholder: (opt as CheckboxOption).textPlaceholder || '請輸入...',
              textRequired: (opt as CheckboxOption).textRequired || false,
              textValue: correspondingValue?.textValue || '' // 這裡會正確設定 textValue
            };
          });
        } else {
          // 向後相容性：處理舊格式 (Category[])
          this.categories = checkboxValues as unknown as Category[];
        }
      }
    } else {
      // 沒有值時，若之前有 _rawOptions 就初始化
      if (this._rawOptions?.length) {
        this.categories = this._rawOptions.map(opt => ({
          name: opt.label,
          value: opt.value,
          selected: false,
          enableTextInput: (opt as CheckboxOption).enableTextInput || false,
          textPlaceholder: (opt as CheckboxOption).textPlaceholder || '請輸入...',
          textRequired: (opt as CheckboxOption).textRequired || false,
          textValue: ''
        }));
      } else {
        this.categories = [];
      }
    }
    
    console.log('[DEBUG] MultiCheckboxComponent processed categories:', this.categories);
    console.log('[DEBUG] MultiCheckboxComponent processed categories - detailed:', JSON.stringify(this.categories, null, 2));
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 若需要禁用，可在此實作 disable 邏輯
  }

  // 新增：取得已選擇的選項（用於只讀模式顯示）
  getSelectedOptions(): Category[] {
    return this.categories.filter(category => category.selected);
  }

  // 新增：判斷是否應該顯示此元件
  get shouldShow(): boolean {
    if (!this.enableWhereCondition) {
      return true; // 如果沒有啟用條件顯示，則永遠顯示
    }
    
    if (!this.whereSourceComponent || !this.whereTargetValue) {
      return true; // 如果條件設定不完整，則預設顯示
    }
    
    // 檢查來源元件的值是否符合目標值
    return this.sourceComponentValue === this.whereTargetValue;
  }
}
